import React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Download, Server, Printer } from 'lucide-react';
import { exportReportPdf } from './pdfExporter';

export function PageLayoutTab({
  reportPageSize, setReportPageSize, reportOrientation, setReportOrientation,
  reportMargins, setReportMargins, reportHeader, setReportHeader,
  reportFooter, setReportFooter, reportName, reportTableSpanPages,
  reportTableRepeatHeader, reportId, visuals, visualResults,
  allFields, getTransformedData, getVizConfig, parameters, paramValues, formatValue,
  fieldOverrides,
  API
}) {
  const [coverPage, setCoverPage] = React.useState(false);
  const [coverSubtitle, setCoverSubtitle] = React.useState('');
  // Report page dimensions are stored in pixels (96px = 1 inch, the web/print
  // standard) but shown to users in inches, which is how people think about
  // paper margins and header/footer bands.
  const PX_PER_IN = 96;
  const toIn = (px) => Math.round(((Number(px) || 0) / PX_PER_IN) * 100) / 100;
  const toPx = (inch) => Math.round((Number(inch) || 0) * PX_PER_IN);
  return (
    <div className="space-y-0 text-[9px]">
      {/* Page Settings */}
      <div className="p-3 border-b border-border/30 space-y-2">
        <p className="text-[9px] text-muted-foreground uppercase tracking-wider font-semibold">Page Settings</p>
        <div className="grid grid-cols-2 gap-2">
          <div><label className="text-[9px] text-muted-foreground">Page Size</label>
            <Select value={reportPageSize} onValueChange={setReportPageSize}><SelectTrigger className="h-6 !text-[9px] bg-muted/30 border-border/30 mt-0.5"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="letter">Letter</SelectItem><SelectItem value="a4">A4</SelectItem><SelectItem value="legal">Legal</SelectItem><SelectItem value="tabloid">Tabloid (11×17")</SelectItem><SelectItem value="a3">A3</SelectItem></SelectContent></Select>
          </div>
          <div><label className="text-[9px] text-muted-foreground">Orientation</label>
            <div className="flex gap-1 mt-0.5">
              <button onClick={() => setReportOrientation('portrait')} className={`flex-1 py-1 text-[9px] rounded border ${reportOrientation === 'portrait' ? 'border-primary/40 bg-primary/10 text-foreground' : 'border-border/30 text-muted-foreground'}`}>Portrait</button>
              <button onClick={() => setReportOrientation('landscape')} className={`flex-1 py-1 text-[9px] rounded border ${reportOrientation === 'landscape' ? 'border-primary/40 bg-primary/10 text-foreground' : 'border-border/30 text-muted-foreground'}`}>Landscape</button>
            </div>
          </div>
        </div>
        <div><label className="text-[9px] text-muted-foreground">Margins (in)</label>
          <div className="grid grid-cols-4 gap-1 mt-0.5">
            {['top', 'right', 'bottom', 'left'].map(s => (
              <div key={s}><label className="text-[8px] text-muted-foreground/80 capitalize">{s}</label><Input type="number" step="0.05" min="0" value={toIn(reportMargins[s])} onChange={e => setReportMargins(p => ({ ...p, [s]: toPx(e.target.value) }))} className="h-6 !text-[9px] bg-muted/20 border-border/20 text-foreground" /></div>
            ))}
          </div>
        </div>
      </div>
      {/* Page Header */}
      <div className="p-3 border-b border-border/30 space-y-2">
        <div className="flex items-center justify-between">
          <p className="text-[9px] text-muted-foreground uppercase tracking-wider font-semibold">Page Header</p>
          <Switch checked={reportHeader.enabled} onCheckedChange={v => setReportHeader(p => ({ ...p, enabled: v }))} />
        </div>
        {reportHeader.enabled && (
          <>
            <div className="flex gap-2">
              <div className="flex-1"><label className="text-[9px] text-muted-foreground">Height (in)</label><Input type="number" step="0.05" min="0" value={toIn(reportHeader.height)} onChange={e => setReportHeader(p => ({ ...p, height: toPx(e.target.value) }))} className="h-6 !text-[9px] bg-muted/30 border-border/30 text-foreground mt-0.5" /></div>
              <label className="flex items-center gap-1 text-[9px] text-muted-foreground mt-3"><input type="checkbox" checked={reportHeader.repeatOnPages} onChange={e => setReportHeader(p => ({ ...p, repeatOnPages: e.target.checked }))} className="w-3 h-3 rounded" /> Repeat</label>
            </div>
            <label className="flex items-center gap-2 text-[9px] text-muted-foreground"><input type="checkbox" checked={reportHeader.showOnFirstPage !== false} onChange={e => setReportHeader(p => ({ ...p, showOnFirstPage: e.target.checked }))} className="w-3 h-3 rounded" /> Display page header on first page</label>
            <p className="text-[9px] text-muted-foreground/70">Add visuals to the Page Header zone on the canvas.</p>
          </>
        )}
      </div>
      {/* Page Footer */}
      <div className="p-3 border-b border-border/30 space-y-2">
        <div className="flex items-center justify-between">
          <p className="text-[9px] text-muted-foreground uppercase tracking-wider font-semibold">Page Footer</p>
          <Switch checked={reportFooter.enabled} onCheckedChange={v => setReportFooter(p => ({ ...p, enabled: v }))} />
        </div>
        {reportFooter.enabled && (
          <>
            <div className="flex-1"><label className="text-[9px] text-muted-foreground">Height (in)</label><Input type="number" step="0.05" min="0" value={toIn(reportFooter.height || 40)} onChange={e => setReportFooter(p => ({ ...p, height: toPx(e.target.value) }))} className="h-6 !text-[9px] bg-muted/30 border-border/30 text-foreground mt-0.5" /></div>
            <div>
              <label className="text-[9px] text-muted-foreground">Page number format</label>
              <Select value={reportFooter.pageNumberFormat || 'verbose'} onValueChange={v => setReportFooter(p => ({ ...p, pageNumberFormat: v }))}>
                <SelectTrigger className="h-6 !text-[9px] bg-muted/30 border-border/30 mt-0.5" data-testid="page-number-format-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None — leave footer blank</SelectItem>
                  <SelectItem value="simple">"1" — just the number</SelectItem>
                  <SelectItem value="of_n">"1 of 12"</SelectItem>
                  <SelectItem value="verbose">"Page 1 of 12"</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <label className="flex items-center gap-2 text-[9px] text-muted-foreground" data-testid="generated-at-toggle">
              <input type="checkbox" checked={!!reportFooter.showGeneratedAt} onChange={e => setReportFooter(p => ({ ...p, showGeneratedAt: e.target.checked }))} className="w-3 h-3 rounded" />
              Show "Generated &lt;date&gt;" stamp at bottom-left
            </label>
            <p className="text-[9px] text-muted-foreground/70">Add visuals to the Page Footer zone on the canvas, or rely on the auto-generated page number + date above.</p>
          </>
        )}
      </div>
      {/* Export */}
      <div className="p-3 space-y-1.5">
        <p className="text-[9px] text-muted-foreground uppercase tracking-wider font-semibold">Export</p>
        <Button variant="outline" size="sm" className="w-full rounded-lg text-xs" onClick={() => exportReportPdf({
          reportOrientation, reportPageSize, reportMargins, reportHeader, reportFooter,
          reportName, reportTableSpanPages, reportTableRepeatHeader,
          visuals, visualResults, allFields, getTransformedData, getVizConfig,
          parameters, paramValues, formatValue, fieldOverrides
        })} data-testid="export-pdf-report"><Download className="w-3 h-3 mr-1" /> Export PDF</Button>
        {reportId && (
          <div className="flex items-center justify-between py-1" data-testid="cover-page-toggle-row">
            <label className="text-[9px] text-muted-foreground">Branded cover page</label>
            <Switch checked={coverPage} onCheckedChange={setCoverPage} data-testid="cover-page-toggle" />
          </div>
        )}
        {reportId && coverPage && (
          <input
            type="text"
            value={coverSubtitle}
            onChange={(e) => setCoverSubtitle(e.target.value)}
            maxLength={200}
            placeholder="Subtitle or author (optional)"
            className="w-full rounded-lg text-xs bg-muted/40 border border-border/40 px-2 py-1.5 mb-1 outline-none focus:border-primary/50"
            data-testid="cover-subtitle-input"
          />
        )}
        {reportId && (
          <Button variant="outline" size="sm" className="w-full rounded-lg text-xs mt-1" onClick={async () => {
            try {
              const { toast } = await import('sonner');
              toast.info('Generating server-side PDF...');
              const response = await fetch(`${API}/api/reports/${reportId}/export-pdf`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ cover: coverPage, cover_subtitle: coverSubtitle })
              });
              if (!response.ok) throw new Error('PDF generation failed');
              const blob = await response.blob();
              const url = window.URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = `${reportName || 'report'}.pdf`;
              a.click();
              window.URL.revokeObjectURL(url);
              toast.success('Server-side PDF exported');
            } catch (err) {
              const { toast } = await import('sonner');
              toast.error('Server PDF export failed: ' + err.message);
            }
          }} data-testid="export-server-pdf-report"><Server className="w-3 h-3 mr-1" /> Server PDF</Button>
        )}
        <Button variant="outline" size="sm" className="w-full rounded-lg text-xs" onClick={() => window.print()} data-testid="export-print-report"><Printer className="w-3 h-3 mr-1" /> Print</Button>
      </div>
    </div>
  );
}
